<template>
    <div class="nav-search-panel">
        <!-- Search field menu -->
        <v-menu offset-y v-model="fieldOpen" :close-on-content-click="false" max-width="400" >
            <template v-slot:activator="{ attrs, on }">
                <div class="nav-search-field" v-bind="attrs" v-on="on">
                    <span>{{ search.field ? search.field.name : '' }}</span>
                    <i class="fas fa-caret-down" :class="{'nav-search-field-open':fieldOpen}"></i>
                </div>
            </template>

            <div class="pa-3 white" v-if="fields && fields.length > 1" >
                <v-expansion-panels
                    style="background-color: white; max-width: 300px"
                >
                    <v-expansion-panel v-for="(category,index) in fields" :key="index">
                        <v-expansion-panel-header
                            style="min-height: 35px;">
                            {{ category.name }}
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                            <v-list-item class="pa-0  pt-1 pb-1" v-for="fld in category.fields" :key="fld.id" link @click="onFieldSelected(category.id, fld)">
                                <v-list-item-title class="pl-3 v-flex">
                                    <div>
                                        {{ fld.name }}
                                    </div>
                                    <v-spacer />
                                    <div class="nav-search-comment primary--text">
                                        {{ fld.comment }}
                                    </div>
                                </v-list-item-title>
                            </v-list-item>
                        </v-expansion-panel-content>
                    </v-expansion-panel>
                </v-expansion-panels>
            </div>

            <div class="white pb-3 pt-3" v-if="fields && fields.length === 1">
                <v-list-item class="pa-0  pt-1 pb-1" v-for="fld in fields[0].fields" :key="fld.id" link @click="onFieldSelected(fields[0].id, fld)">
                    <v-list-item-title class="pl-3">
                        <div>
                            {{ fld.name }}
                        </div>
                        <div>
                        </div>
                    </v-list-item-title>
                </v-list-item>
            </div>
        </v-menu>
        <!-- Search field menu -->

        <!-- Search criteria input -->
        <div class="nav-search-input" :class="error">
            <label>
                <input
                    ref             = "searchinput"
                    @keypress.enter = "onEnter"
                    @focusin        = "onFocusIn"
                    @focusout       = "onFocusOut"
                    type            = "text"
                    v-model         = "search.criteria"
                    @keydown        = "onKeydown"
                    @keyup          = "onKeyup"
                />
            </label>
            <div class="nav-search-icon">
                <a @click="onSearch"><i class="fas fa-search"></i></a>
            </div>
        </div>
        <!-- Search criteria input -->

        <div v-if="search.field && search.field.category === 'site'">
            <c-app-unit-filter
                @change = "onChangeFilter"
            />
        </div>

        <div v-if="search.field && search.field.category === 'incident'">
            <c-app-incident-filter
                @change = "onChangeFilter"
            />
        </div>

        <!-- Hits count popup -->
        <div 
            class="nav-search-hit-panel secondary white--text" 
            v-if="showHits" 
            @click="onSearch"
            @mousedown.prevent
            style="cursor: pointer;"
        >
            <span>{{ hits }}{{ i18n ("hits") }}</span>
        </div>
        <!-- Hits count popup -->

    </div>
</template>

<script>

import Core from "@/modules/core/service/Core";
import Search from "@/modules/core/service/private/SearchRequest";
import CAppUnitFilter from "@/modules/core/components/app/CAppUnitFilter";
import CAppIncidentFilter from "@/modules/core/components/app/CAppIncidentFilter";

const Const = {
    search: {
        minLength: 1,
    },

    route: {
        site    : "sitelist",
        incident: "incidents"
    }
}

export default {
    name: "CAppHomeSearch",
    components: {
        CAppIncidentFilter,
        CAppUnitFilter
    },

    props: {
        categories: {
            required: false,
            type    : Array,
            default : function () {
                return null;
            }
        },

        searchOnFocus: {
            required: false,
            type    : Boolean,
            default : function () {
                return false;
            }
        }
    },

    data: () => ({
        fieldOpen: false,
        inSearch : false,
        category : null,
        hits     : 0,
        error    : null,
        search   : {
            criteria : "",
            field    : {},
            filter   : null
        }
    }),

    watch: {
        criteria: function () {
            this.processInput();
        }
    },

    created: function () {
        this.init();
        this.$bus.$on(this.$events.opensearch, this.onOpenFields)
    },

    beforeDestroy() {
        this.$bus.$off(this.$events.opensearch, this.onOpenFields)
    },

    computed: {
        criteria () {
            return this.search.criteria;
        },

        liftmanager() {
            return Core.role.liftmanager();
        },

        fields() {
            if (this.liftmanager) {
                if (!this.categories) {
                    return this.$store.getters.fields;
                }

                const fields = this.$store.getters.fields;
                if (fields instanceof Array) {
                    return fields.filter (f => this.categories.includes(f.id));
                }

                return null;
            }

            const fields = this.$store.getters.fields;
            if (fields instanceof Array) {
                return fields.filter (f => f.id === 'site');
            }

            return null;
        },

        showHits: function () {
            // Show hits when searching OR when there are results to display
            return this.search.criteria.length >= Const.search.minLength && (this.inSearch === true || this.hits > 0);
        }
    },

    methods: {
        i18n(resource) {
            return this.$vuetify.lang.t("$vuetify.navtop.search." + resource);
        },

        onChangeFilter (filter) {
            this.inSearch = true;
            this.search.filter = filter;
            this.save();
            this.countHits();
            this.onSearch();
        },

        async init () {
            await this.$store.dispatch('getFields');
            const searchFields = this.fields;
            if (!searchFields || searchFields.length === 0) {
                return;
            }

            this.search = this.$store.getters.search;
            if (!this.search.field) {
                const [firstCategory] = searchFields;
                this.search.field = firstCategory.fields[0];
                this.category = firstCategory.id;
            } else {
                const stored = this.search.field;
                const hasField = cat => cat.fields && cat.fields.some(f => f.id === stored.id);
                // field ids are not unique across categories (e.g. externalIdQuery): prefer the stored category
                const owningCategory = (stored.category && searchFields.find(cat => cat.id === stored.category && hasField(cat)))
                    || (stored.category ? null : searchFields.find(hasField));
                this.category = owningCategory ? owningCategory.id : searchFields[0].id;
            }
        },

        async countHits (afterCount) {
            if (!this.search.criteria || this.search.criteria.length === 0) {
                return;
            }

            const payload = {
                category: this.category,
                filter   : this.getFilter()
            };

            await this.$store.dispatch('countHits', payload);
            this.hits = this.$store.getters.hitCount;

            if (afterCount) {
                afterCount ();
            }
        },

        onFieldSelected: function (category, field) {
            this.error = false;
            this.category     = category;
            this.search.field = field;

            if (this.search && this.search.field && this.search.field.numeric) {
                this.search.criteria = this.search.criteria.trim();
                if (this.search.criteria && !/^\d+$/.test(this.search.criteria)) {
                    this.error = "red"
                }
            }

            this.save();
            if (this.search.criteria.length >= Const.search.minLength) {
                this.countHits ();
                this.onSearch();
            }

            this.fieldOpen = false;
        },

        onFocusIn () {
            if (this.searchOnFocus) {
                if (!this.search.criteria || this.search.criteria.length === 0) {
                    this.search.criteria = "*";
                }

                this.countHits (() => {
                    this.onSearch()
                });
            }

       //     Core.nav.home(this);
            this.inSearch = true;
            this.countHits ();
        },

        onFocusOut () {
            // Delay hiding to allow clicking the hits panel
            setTimeout(() => {
                this.inSearch = false;
            }, 200);
        },

        onEnter: function () {
            this.onSearch();
        },

        onKeydown (event) {
            if (!event.ctrlKey) {
                return;
            }

            switch (event.code) {
                case "KeyI":
                case "KeyM":
                case "KeyF":
                case "KeyA":
                case "KeyB":
                case "KeyO":
                case "KeyE":
                case "KeyR":
                    event.preventDefault();
                    break;
            }

        },

        onKeyup (event) {
            if (!event.ctrlKey) {
                return;
            }

            event.preventDefault();

            let categoryId = null;
            let fieldId    = null;

            switch (event.code) {
                case "KeyI":
                    categoryId = "incident";
                    fieldId    = "incidentIdQuery"
                    break;
                case "KeyM":
                    categoryId = "incident";
                    fieldId    = "incidentMatchAllQuery"
                    break;
                case "KeyF":
                    categoryId = "site";
                    fieldId    = "siteFabricNoQuery"
                    break;
                case "KeyA":
                    categoryId = "site";
                    fieldId    = "siteMatchAllQuery"
                    break;
                case "KeyR":
                    categoryId = "incident";
                    fieldId    = "incidentInvoiceNoQuery"
                    break;
                case "KeyB":
                    categoryId = "incident";
                    fieldId    = "incidentBidNoQuery"
                    break;
                case "KeyO":
                    categoryId = "incident";
                    fieldId    = "incidentOrderNoQuery"
                    break;
                case "KeyE":
                    categoryId = "incident";
                    fieldId    = "incidentTypeQuery"
                    break;
            }

            if (!categoryId) {
                return;
            }

            event.preventDefault();
            const field = this.getField(categoryId, fieldId);
            if (field) {
                this.onFieldSelected(field.category, field);
            }
        },

        onSearch: function () {
            if (this.hits === 0 || !this.search.criteria) {
                // shake input
                return;
            }

            const filter = this.getFilter();
            Core.nav.push(this, Const.route[this.category], filter);
        },

        onOpenFields () {
            this.$nextTick(() => {
                this.$refs.searchinput.focus();
            })

            this.fieldOpen = true;
        },

        save () {
            this.$store.dispatch("setSearch", this.search);
        },

        getFilter () {
            const filter = {
                field   : this.search.field.id,
                value   : this.search.criteria ? this.search.criteria : this.search.criteria.trim(),
                customer: Core.preferences.customer(this)
            };

            Object.assign(filter, this.search.filter);
            // map to the search-svc names (e.g. ignoreDeleted -> includeDeleted)
            return Search.filter(this.category, filter);
        },

        processInput () {
            if (this.search && this.search.field && this.search.field.numeric) {
                this.search.criteria = this.search.criteria.trim();
                if (this.search.criteria && !/^\d+$/.test(this.search.criteria)) {
                    this.error = "red"
                    return;
                }
            }

            this.error = null;
            this.save();
            this.countHits();
        },

        getField (categoryId, fieldId) {
            const config = this.$store.getters.fields;
            const categories = config.find (category => category.id === categoryId);
            if (!categories) {
                return null;
            }

            return categories.fields.find(field => field.id === fieldId);
        }
    }
}

</script>

<style scoped>

::v-deep .v-sheet.v-list {
    border-radius: 0;
    padding-left: 0;
    padding-right: 0;
    padding-top: 10px;
}

::v-deep .v-list-item {
    min-height: 36px;
    padding: 0 5px;
}

::v-deep .theme--light.v-list-item:not(.v-list-item--active):not(.v-list-item--disabled) {
    color: black!important;
}

::v-deep .theme--light.v-list-item:not(.v-list-item--active):not(.v-list-item--disabled):hover {
    color: white!important;
    border-radius: .125rem;
    -webkit-box-shadow: 0 5px 11px 0 rgba(0, 0, 0, 0.18), 0 4px 15px 0 rgba(0, 0, 0, 0.15);
    box-shadow: 0 5px 11px 0 rgba(0, 0, 0, 0.18), 0 4px 15px 0 rgba(0, 0, 0, 0.15);
    -webkit-transition: all 0.1s linear;
    transition: all 0.1s linear;
}

::v-deep .v-list-item__title {
    font-size: 11pt;
}

.nav-search-comment {
    padding-top: 2px;
    padding-right: 4px;
    font-size: 12px;
}

.nav-search-hit-panel {
    width: 125px;
    height: 32px;
    font-size: 12pt;
    padding-top: 4px;
    padding-left: 5px;
    margin-left: 5px;
    margin-right: 5px;
    border-radius: 4px;
    text-align: center;
}

.nav-search-panel {
    display: flex;
    height: 35px;
}

.nav-search-input,
.nav-search-field{
    background-color: white;
    color: black;
    border-style: solid;
    border-color: lightgray;
    border-width: 1px;
    border-radius: 2px;
    letter-spacing: normal;
    font-size: 11pt!important;
    line-height: 1.5;
    margin-right: 5px;
    padding-left: 10px;
    height: 32px;
    width: 180pt;
    text-align: left;
    padding-top: 5px;
}

.nav-search-field {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.nav-search-field i {
    float: right;
    padding-right: 10px!important;
}

.nav-search-field-open  {
    transform: rotate(180deg) translate(10px);
    -webkit-transition: all 150ms ease-in 0s;
    transition: all 150ms ease-in 0s;
}

.nav-search-input input {
    outline: none;
    width: 100%;
}

::v-deep .v-btn__content {
    align-items: start;
}

::v-deep .v-menu__content {
    padding-left: 10px;
    padding-right: 10px;
    background-color: white;
    min-width: 16rem !important;
}

.nav-search-icon {
    position: relative;
    top: -23px;
    left: 204px;
    width: 20px;
}

.nav-search-icon i {
    color: gray;
    font-size: 11pt!important;
}

::v-deep .v-expansion-panel-content__wrap {
    padding: 0;
    padding-bottom: 10px;
}

::v-deep .v-expansion-panel-header {
    padding: 15px;
}

</style>